// Multiplexed /api/stream client for the SPA (JOBE-0045.3).
//
// One EventSource per browser tab. Started lazily on the first
// startStream() call (typically from the Shell's mount effect),
// stopped on stopStream() if/when the SPA unloads. EventSource's
// built-in reconnect handles transient network failures; the
// browser also sends Last-Event-ID automatically from the most
// recent `id:` line, so per-replica resume works without bespoke
// code.

let storeRef = null;
let es = null;
let openCount = 0;

export function setStoreRef(useStore) {
  storeRef = useStore;
}

// startStream opens the EventSource on the first call and refcounts
// from there. Returns a stop function that the caller should invoke
// on unmount.
export function startStream() {
  openCount += 1;
  if (es) return makeStop();
  es = new EventSource('/api/stream', { withCredentials: true });
  es.addEventListener('open', () => {
    storeRef?.getState().setStreamStatus('connected');
  });
  es.addEventListener('error', () => {
    // EventSource errors fire on transient drops too; readyState
    // distinguishes a hard close (CLOSED) from an in-progress retry
    // (CONNECTING).
    if (!es) return;
    if (es.readyState === EventSource.CLOSED) {
      storeRef?.getState().setStreamStatus('down');
    } else {
      storeRef?.getState().setStreamStatus('connecting');
    }
  });

  // Topic dispatch. The BFF sets `event: <topic>` on every line, so
  // we attach listeners for the stable non-chat topics the SPA cares
  // about here. Chat — both human-authored and agent-authored — flows
  // through the JOBE-0053.6 WebSocket bridge against the per-channel
  // JetStream stream, not SSE (JOBE-0060.7). SSE is reserved for
  // activity / feed / notifications / tasks.
  attach(es, 'agents.activity', (data) => storeRef?.getState().onActivityEvent(data));
  attach(es, 'tasks.changed',   () => storeRef?.getState().refetchTasks());
  attach(es, 'feed.appended',   (data) => storeRef?.getState().appendFeed(data));
  attach(es, 'notifications.added', (data) => storeRef?.getState().addNotification(data));

  return makeStop();
}

function makeStop() {
  return function stopStream() {
    openCount -= 1;
    if (openCount > 0) return;
    if (!es) return;
    es.close();
    es = null;
  };
}

// The BFF wraps every event in a StreamEvent envelope:
//   { type: "delta"|"message"|..., ts: "...", payload: {...} }
// The id and topic ride on the SSE `id:` and `event:` lines (struct
// fields are `json:"-"`). Handlers want the inner payload, so we unwrap
// here. `evt.type` is exposed as a second arg for handlers that need
// to disambiguate (chat: delta vs message vs error).
function attach(source, topic, fn) {
  source.addEventListener(topic, (e) => {
    let env;
    try { env = JSON.parse(e.data); } catch { return; }
    const payload = env && env.payload !== undefined ? env.payload : env;
    fn(payload, env && env.type);
  });
}
